import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Book, 
  Key, 
  Search, 
  Plus, 
  Copy, 
  Check,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Code,
  Database,
  Calendar,
  Image,
  MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "sonner";

export default function ApiDocsPage() {
  const [copiedCode, setCopiedCode] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    kb: true,
    availability: true,
    reservation: true
  });

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    toast.success("Copié !");
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const CodeBlock = ({ code, language = "bash", id }) => (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{code}</code>
      </pre>
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-gray-700 text-white"
        onClick={() => copyToClipboard(code, id)}
      >
        {copiedCode === id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </Button>
    </div>
  );

  const JsonBlock = ({ data, id }) => (
    <div className="relative group">
      <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
        <code>{JSON.stringify(data, null, 2)}</code>
      </pre>
      <Button
        size="icon"
        variant="ghost"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 hover:bg-gray-700 text-white"
        onClick={() => copyToClipboard(JSON.stringify(data, null, 2), id)}
      >
        {copiedCode === id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center">
              <Book className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-xl">ResaFlow.ai API</h1>
              <p className="text-sm text-gray-500">Documentation pour Chatbot IA</p>
            </div>
          </div>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            v1.1
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-8">
        {/* Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5 text-cyan-500" />
              Vue d'ensemble
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              ResaFlow.ai expose trois APIs pour intégrer un chatbot IA à votre système de réservation.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-900">Knowledge Base</span>
                </div>
                <p className="text-sm text-blue-700">Catalogue, prix, images, politiques</p>
              </div>
              <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-900">Availability</span>
                </div>
                <p className="text-sm text-green-700">Vérifier les disponibilités temps réel</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-xl border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Plus className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-900">Reservation</span>
                </div>
                <p className="text-sm text-purple-700">Créer une réservation</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Authentication */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-500" />
              Authentification
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <h4 className="font-semibold text-amber-900 mb-2">Token Knowledge Base</h4>
                <p className="text-sm text-amber-700 mb-2">Pour accéder au catalogue</p>
                <code className="text-xs bg-amber-100 px-2 py-1 rounded">?access=VOTRE_TOKEN</code>
              </div>
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                <h4 className="font-semibold text-amber-900 mb-2">Clé API</h4>
                <p className="text-sm text-amber-700 mb-2">Pour disponibilités et réservations</p>
                <code className="text-xs bg-amber-100 px-2 py-1 rounded">Authorization: Bearer gf974_xxx</code>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              📍 Obtenez vos credentials dans <strong>Paramètres → Base de Connaissances IA</strong> et <strong>Paramètres → Clés API</strong>
            </p>
          </CardContent>
        </Card>

        {/* Knowledge Base API */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-xl"
            onClick={() => toggleSection('kb')}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" />
                API Knowledge Base
                <Badge className="bg-blue-100 text-blue-700">GET</Badge>
              </div>
              {expandedSections.kb ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.kb && (
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Endpoint</h4>
                <CodeBlock 
                  code="GET https://resaflow.ai/api/kb/{org_id}?access={token}" 
                  id="kb-endpoint"
                />
              </div>

              <div>
                <h4 className="font-semibold mb-2">Exemple curl</h4>
                <CodeBlock 
                  code={`curl "https://resaflow.ai/api/kb/org_xxx?access=VOTRE_TOKEN"`}
                  id="kb-curl"
                />
              </div>

              <div>
                <h4 className="font-semibold mb-2">Réponse</h4>
                <JsonBlock 
                  id="kb-response"
                  data={{
                    priority_instructions: {
                      active: true,
                      title: "Promo été",
                      instructions: "Mentionner la promo -20%..."
                    },
                    company: {
                      name: "Jeroloc",
                      description: "Location de structures gonflables",
                      location: "Saint-Denis, La Réunion",
                      time_slots: [
                        { id: "matin", label: "Matin", hours: "8h-13h" },
                        { id: "apres-midi", label: "Après-midi", hours: "14h-19h" },
                        { id: "journee", label: "Journée complète", hours: "8h-19h" }
                      ]
                    },
                    structures: [
                      {
                        id: "str_chateau-princesse",
                        name: "Château Princesse",
                        dimensions: "4m x 4m",
                        image_url: "https://images.resaflow.ai/..."
                      }
                    ],
                    packs: [{ id: "pack_anniversaire-20", name: "Pack Anniversaire", price: 150 }],
                    options: [{ id: "opt_barbe-papa", name: "Barbe à papa", price: 70 }],
                    catalog_images: [
                      {
                        image_url: "https://images.resaflow.ai/...",
                        description: "Photo anniversaire"
                      }
                    ]
                  }}
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Availability API */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-xl"
            onClick={() => toggleSection('availability')}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-500" />
                API Availability Engine
                <Badge className="bg-green-100 text-green-700">POST</Badge>
              </div>
              {expandedSections.availability ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.availability && (
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Endpoint</h4>
                <CodeBlock 
                  code={`POST https://resaflow.ai/api/availability/query
Authorization: Bearer {CLE_API}
Content-Type: application/json`}
                  id="avail-endpoint"
                />
              </div>

              {/* Query Type 1 */}
              <div className="p-4 bg-gray-50 rounded-xl border">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge variant="outline">1</Badge>
                  check_structure - Vérifier une structure
                </h4>
                <div className="space-y-3">
                  <JsonBlock 
                    id="check-request"
                    data={{
                      query_type: "check_structure",
                      structure_id: "str_abc123",
                      event_date: "2026-03-15"
                    }}
                  />
                  <p className="text-sm text-gray-500">Réponse : disponibilité par créneau (matin, après-midi, journée)</p>
                </div>
              </div>

              {/* Query Type 2 */}
              <div className="p-4 bg-gray-50 rounded-xl border">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge variant="outline">2</Badge>
                  list_available - Lister les structures disponibles
                </h4>
                <div className="space-y-3">
                  <JsonBlock 
                    id="list-request"
                    data={{
                      query_type: "list_available",
                      event_date: "2026-03-15"
                    }}
                  />
                  <p className="text-sm text-gray-500">Réponse : liste des structures avec créneaux disponibles</p>
                </div>
              </div>

              {/* Query Type 3 */}
              <div className="p-4 bg-gray-50 rounded-xl border">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Badge variant="outline">3</Badge>
                  next_available - Prochaines disponibilités
                </h4>
                <div className="space-y-3">
                  <JsonBlock 
                    id="next-request"
                    data={{
                      query_type: "next_available",
                      structure_id: "str_abc123"
                    }}
                  />
                  <p className="text-sm text-gray-500">Réponse : prochaines dates disponibles (30 jours)</p>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Create Reservation API */}
        <Card>
          <CardHeader 
            className="cursor-pointer hover:bg-gray-50 transition-colors rounded-t-xl"
            onClick={() => toggleSection('reservation')}
          >
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-purple-500" />
                API Create Reservation
                <Badge className="bg-purple-100 text-purple-700">POST</Badge>
              </div>
              {expandedSections.reservation ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </CardTitle>
          </CardHeader>
          {expandedSections.reservation && (
            <CardContent className="space-y-6">
              <div>
                <h4 className="font-semibold mb-2">Endpoint</h4>
                <CodeBlock 
                  code={`POST https://resaflow.ai/api/availability/create-reservation
Authorization: Bearer {CLE_API}
Content-Type: application/json`}
                  id="create-endpoint"
                />
              </div>

              <div>
                <h4 className="font-semibold mb-2">Body (Requête)</h4>
                <JsonBlock 
                  id="create-request"
                  data={{
                    structure_id: "org_xxx_château-princesse",
                    event_date: "2026-03-20",
                    time_slot: "apres-midi",
                    offer_id: "pack_xyz",
                    client_name: "Jean Dupont",
                    client_phone: "0692123456",
                    client_email: "jean@email.com",
                    client_address: "10 rue des Lilas",
                    notes: "Anniversaire 8 ans",
                    source: "chatbot_messenger"
                  }}
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-semibold mb-2 text-green-700">✓ Champs obligatoires</h4>
                  <ul className="text-sm space-y-1">
                    <li><code className="bg-gray-100 px-1 rounded">structure_id</code> - ID de la structure</li>
                    <li><code className="bg-gray-100 px-1 rounded">event_date</code> - Date (YYYY-MM-DD)</li>
                    <li><code className="bg-gray-100 px-1 rounded">time_slot</code> - matin/apres-midi/journee</li>
                    <li><code className="bg-gray-100 px-1 rounded">client_name</code> - Nom du client</li>
                    <li><code className="bg-gray-100 px-1 rounded">client_phone</code> - Téléphone</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-gray-500">○ Champs optionnels</h4>
                  <ul className="text-sm space-y-1 text-gray-600">
                    <li><code className="bg-gray-100 px-1 rounded">offer_id</code> - ID du pack</li>
                    <li><code className="bg-gray-100 px-1 rounded">options</code> - Liste d'IDs d'options</li>
                    <li><code className="bg-gray-100 px-1 rounded">client_email</code></li>
                    <li><code className="bg-gray-100 px-1 rounded">client_address</code></li>
                    <li><code className="bg-gray-100 px-1 rounded">notes</code> - Commentaires</li>
                    <li><code className="bg-gray-100 px-1 rounded">source</code> - Ex: chatbot_messenger</li>
                  </ul>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Réponse succès</h4>
                <JsonBlock 
                  id="create-success"
                  data={{
                    success: true,
                    reservation_id: "res_abc123xyz",
                    status: "DEMANDE_EN_LIGNE",
                    structure: { id: "...", name: "Château Princesse" },
                    date: "2026-03-20",
                    time_slot: "apres-midi",
                    total_price: 150,
                    message: "Réservation créée avec succès."
                  }}
                />
              </div>

              <div>
                <h4 className="font-semibold mb-2 text-red-600">Réponse erreur (conflit)</h4>
                <JsonBlock 
                  id="create-error"
                  data={{
                    success: false,
                    error: "slot_unavailable",
                    message: "Ce créneau n'est plus disponible"
                  }}
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Error Codes */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Code className="w-5 h-5 text-red-500" />
              Codes d'erreur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Code</th>
                    <th className="text-left py-2 px-3">Signification</th>
                    <th className="text-left py-2 px-3">Solution</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-2 px-3"><Badge variant="outline">401</Badge></td>
                    <td className="py-2 px-3">Token/Clé invalide</td>
                    <td className="py-2 px-3 text-gray-500">Vérifier les credentials</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3"><Badge variant="outline">403</Badge></td>
                    <td className="py-2 px-3">Accès refusé</td>
                    <td className="py-2 px-3 text-gray-500">Vérifier les permissions</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3"><Badge variant="outline">404</Badge></td>
                    <td className="py-2 px-3">Ressource non trouvée</td>
                    <td className="py-2 px-3 text-gray-500">Vérifier l'org_id ou structure_id</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2 px-3"><Badge variant="outline">409</Badge></td>
                    <td className="py-2 px-3">Conflit (créneau pris)</td>
                    <td className="py-2 px-3 text-gray-500">Proposer une autre date/créneau</td>
                  </tr>
                  <tr>
                    <td className="py-2 px-3"><Badge variant="outline">429</Badge></td>
                    <td className="py-2 px-3">Rate limit dépassé</td>
                    <td className="py-2 px-3 text-gray-500">Attendre ou augmenter le quota</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Chatbot Integration Tips */}
        <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-cyan-600" />
              Intégration Chatbot - Conseils
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-sm mb-1">💾 Cache la Knowledge Base</h4>
                <p className="text-xs text-gray-600">Rafraîchir toutes les heures max (données peu volatiles)</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-sm mb-1">⚡ Vérification temps réel</h4>
                <p className="text-xs text-gray-600">Appeler l'Availability API juste avant de confirmer</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-sm mb-1">🖼️ URLs images publiques</h4>
                <p className="text-xs text-gray-600">Les images sont sur CDN, affichables directement</p>
              </div>
              <div className="p-3 bg-white rounded-lg">
                <h4 className="font-semibold text-sm mb-1">📊 Rate limit</h4>
                <p className="text-xs text-gray-600">200 appels/mois par défaut, extensible</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-gray-500 py-8">
          <p>Documentation ResaFlow.ai API v1.1</p>
          <p className="mt-1">
            <a href="/" className="text-cyan-600 hover:underline inline-flex items-center gap-1">
              Retour à l'accueil <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </main>
      
      <Toaster position="top-center" richColors />
    </div>
  );
}
